import { useMemo } from 'react';
import { Activity, Check, ChevronRight, Clock, Loader2, Minus, Moon, Plus, RefreshCw, Sparkles, Target, Zap } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DashboardRings } from '@/components/dashboard/DashboardRings';
import { PROGRAM } from '@/data/program';
import { WATER_GLASSES_TARGET, WATER_GLASS_ML } from '@/data/constants';
import { buildResolvedDay, estimateDurationSec } from '@/lib/program';
import { calcBMI, bmiCategory, calcTDEE, calcKcalTarget, calcMacroTargets, calcBedtime, todaysWakeTime } from '@/lib/calc';
import { formatDate } from '@/lib/format';
import { hapticTap } from '@/lib/haptic';
import { useStore } from '@/hooks/useStore';
import type { Profile } from '@/types';

export interface TodayScreenProps {
  profile: Profile;
  weekIdx: 1 | 2 | 3 | 4;
  jourIdx: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onOpenTraining: () => void;
  onOpenCoach: () => void;
  onRefreshTip: () => void;
  refreshingTip: boolean;
}

export const TodayScreen = ({
  profile,
  weekIdx,
  jourIdx,
  onOpenTraining,
  onOpenCoach,
  onRefreshTip,
  refreshingTip,
}: TodayScreenProps) => {
  const todayLog = useStore(s => s.todayLog);
  const patchTodayLog = useStore(s => s.patchTodayLog);
  const tip = useStore(s => s.tip);

  const session = useMemo(() => {
    const day = PROGRAM[weekIdx][jourIdx];
    return buildResolvedDay(day, profile.equipment, profile.priorityZones, profile.level);
  }, [weekIdx, jourIdx, profile.equipment, profile.priorityZones, profile.level]);

  const tdee = calcTDEE(profile.currentWeight, profile.height, profile.age, profile.sex);
  const kcalTarget = calcKcalTarget(tdee);
  const macros = calcMacroTargets(kcalTarget, profile.currentWeight);

  const wake = todaysWakeTime(profile);
  const bed = calcBedtime(wake);

  const water = todayLog.water ?? 0;
  const meals = todayLog.meals ?? {};
  const validatedMeals = Object.values(meals).filter(m => m?.done);
  const todayKcal = validatedMeals.reduce((a, m) => a + (m?.kcal ?? 0), 0);
  const todayProt = validatedMeals.reduce((a, m) => a + (m?.p ?? 0), 0);

  const bmi = calcBMI(profile.currentWeight, profile.height);
  const bmiCat = bmiCategory(bmi);
  const sessionDurationSec = estimateDurationSec(session);

  const updateWater = (delta: number) => {
    hapticTap();
    void patchTodayLog({ water: Math.max(0, water + delta) });
  };

  return (
    <div className="px-4 py-6 space-y-4 pb-24 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500">{formatDate(new Date())}</div>
        <h1 className="text-2xl font-light mt-1">Bonjour, {profile.name}.</h1>
      </div>

      {/* Anneaux Apple Watch */}
      <Card>
        <div className="flex flex-col items-center">
          <DashboardRings
            kcal={{ value: todayKcal, target: kcalTarget }}
            protein={{ value: todayProt, target: macros.protein }}
            water={{ value: water, target: WATER_GLASSES_TARGET }}
          />
        </div>
      </Card>

      {/* Séance du jour */}
      <Card>
        <div className="flex justify-between items-start mb-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-accent-400">Séance · semaine {weekIdx}</div>
            <div className="text-lg font-medium mt-1 truncate">{session.titre}</div>
          </div>
          <div
            className={[
              'text-xs px-2 py-1 rounded-full',
              session.tag === 'boxe'  ? 'bg-rose-900/30 text-rose-300' :
              session.tag === 'muscu' ? 'bg-amber-900/30 text-amber-300' :
              session.tag === 'repos' ? 'bg-zinc-800 text-zinc-400' :
              'bg-emerald-900/30 text-emerald-300',
            ].join(' ')}
          >
            {session.tag}
          </div>
        </div>
        <div className="text-sm text-zinc-400 mb-4 tabular-nums">
          {session.items.length} exercices · ~{Math.round(sessionDurationSec / 60)} min
        </div>
        <Button variant="primary" fullWidth onClick={onOpenTraining}>
          {todayLog.workoutCompleted ? <><Check className="w-4 h-4" /> Séance terminée — revoir</> : <>Démarrer la séance</>}
        </Button>
      </Card>

      {/* Coach IA */}
      <button onClick={onOpenCoach} className="w-full text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60">
        <Card className="bg-gradient-to-br from-accent-500/10 to-accent-500/5 border-accent-500/30 hover:border-accent-500/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="bg-accent-500/15 rounded-full p-2.5">
              <Sparkles className="w-5 h-5 text-accent-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium">Parler à ton coach</div>
              <div className="text-xs text-zinc-400 mt-0.5">Sport &amp; boxe · Vie &amp; hygiène</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </div>
        </Card>
      </button>

      {/* Hydratation */}
      <Card>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Hydratation</div>
            <div className="text-sm mt-0.5 tabular-nums">
              {((water * WATER_GLASS_ML) / 1000).toFixed(2)} L / 3 L
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="secondary" iconOnly aria-label="Retirer un verre" onClick={() => updateWater(-1)}>
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-2xl font-light tabular-nums w-10 text-center">{water}</span>
            <Button variant="primary" iconOnly aria-label="Ajouter un verre" onClick={() => updateWater(1)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={<Target className="w-4 h-4 text-accent-400" />} label="IMC"
                  value={bmi.toFixed(1)} sub={<span className={bmiCat.color}>{bmiCat.label}</span>} />
        <StatCard icon={<Zap className="w-4 h-4 text-accent-400" />} label="Calories"
                  value={`${kcalTarget}`} sub={<>cible/jour (+300)</>} />
        <StatCard icon={<Moon className="w-4 h-4 text-accent-400" />} label="Coucher"
                  value={<span className="tabular-nums">{bed}</span>} sub={<>pour 8h, réveil {wake}</>} />
        <StatCard icon={<Activity className="w-4 h-4 text-accent-400" />} label="Protéines"
                  value={`${macros.protein}g`} sub={<>cible/jour</>} />
      </div>

      {/* Tip du jour */}
      <Card>
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent-400" />
            <div className="text-xs uppercase tracking-wider text-zinc-500">Tip du jour</div>
          </div>
          <Button
            variant="ghost"
            iconOnly
            aria-label="Régénérer le tip du jour"
            onClick={onRefreshTip}
            disabled={refreshingTip}
          >
            {refreshingTip ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed min-h-[2.5rem]">{tip}</p>
      </Card>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub: React.ReactNode }) => (
  <Card>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
    <div className="text-2xl font-light tabular-nums">{value}</div>
    <div className="text-xs text-zinc-500">{sub}</div>
  </Card>
);

void Clock;
